/** The opt-in bar for Google Analytics.
 *
 *  „Ablehnen" is the same size, weight and distance from the reader as
 *  „Akzeptieren" - German supervisory authorities treat a refusal that is
 *  harder to reach than an acceptance as no valid consent at all. The bar
 *  does not block the page either: a visitor who ignores it can read the
 *  whole site, and nothing loads until they actually choose.
 *
 *  The explanatory sentence is deliberately set small and quiet so the bar
 *  sits lightly under the page. Two floors it must not go below, because
 *  consent has to be *informed* to be consent at all: 12px, and the slate
 *  tone on paper, which clears WCAG AA. Shrinking or fading it past that
 *  would not make the bar tidier, it would make the consent worthless.
 */

import { Link } from 'react-router-dom'
import { pathFor, t } from '../lib/i18n'
import { useConsent } from '../lib/gtag'
import { useLocale } from '../lib/hooks'
import { PlainButton } from './ui'

export default function ConsentBanner() {
  const locale = useLocale()
  const strings = t(locale).consent
  const { asking, choose } = useConsent()

  if (!asking) return null

  return (
    <div
      role="dialog"
      aria-label={strings.title}
      data-testid="consent-banner"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-line bg-paper shadow-[0_-1px_6px_0_rgba(0,0,0,0.05)]"
    >
      <div className="mx-auto flex max-w-[1200px] flex-col gap-3 px-6 py-3.5 md:flex-row md:items-center md:justify-between md:gap-8 md:px-10">
        <p className="m-0 flex-1 font-sans text-[13px] leading-[1.5] text-slate">
          {strings.body}{' '}
          <Link
            to={pathFor(locale, 'datenschutz')}
            className="underline hover:no-underline"
            data-testid="consent-privacy-link"
          >
            {strings.more}
          </Link>
        </p>

        <div className="flex shrink-0 gap-3">
          <PlainButton
            type="button"
            variant="ghost"
            onClick={() => choose('denied')}
            data-testid="consent-decline"
          >
            {strings.decline}
          </PlainButton>
          <PlainButton
            type="button"
            variant="primary"
            onClick={() => choose('granted')}
            data-testid="consent-accept"
          >
            {strings.accept}
          </PlainButton>
        </div>
      </div>
    </div>
  )
}
